/**
 * Internal hook: profile CRUD, CNIC, onboarding operations.
 * Extracted from UserContext to reduce file size.
 */
import type { Session } from '@supabase/supabase-js';
import { authService } from '@/services/supabase';
import type { User, CNICFiles, Resource, JobApplication } from './types';

export function useProfileOperations(
  user: User | null,
  setUser: React.Dispatch<React.SetStateAction<User | null>>,
  session: Session | null,
) {
  const updateUser = async (updates: Partial<User>) => {
    if (user && session?.user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      
      try {
        await authService.updateProfile(session.user.id, {
          full_name: updates.name,
          phone: updates.phone,
          avatar_url: updates.profileImage,
          city: updates.location,
        });
      } catch (error) {
        if (import.meta.env.DEV) console.error('Failed to sync profile update:', error);
      }
    }
  };

  const updateCNICVerification = (verified: boolean, files?: CNICFiles) => {
    if (user) {
      setUser({
        ...user,
        cnicVerified: verified,
        cnicFiles: files || user.cnicFiles
      });
    }
  };

  const uploadCNICFiles = async (frontFile: File, backFile: File): Promise<boolean> => {
    if (!user || !session?.user) return false;
    
    try {
      const { documentsService } = await import('@/services/supabase');
      const { documents, error } = await documentsService.uploadCNIC(session.user.id, frontFile, backFile);
      
      if (error || !documents) {
        if (import.meta.env.DEV) console.error('CNIC upload failed:', error);
        return false;
      }

      setUser({ ...user, cnicUploaded: true, cnicVerified: false });
      return true;
    } catch (error) {
      if (import.meta.env.DEV) console.error('CNIC files upload failed:', error);
      return false;
    }
  };

  const uploadProfileImage = async (file: File): Promise<boolean> => {
    if (!user || !session?.user) return false;
    
    try {
      const { documentsService } = await import('@/services/supabase');
      const { url, error } = await documentsService.uploadProfilePhoto(session.user.id, file);
      
      if (error || !url) {
        if (import.meta.env.DEV) console.error('Profile image upload failed:', error);
        return false;
      }
      
      setUser({ ...user, profileImage: url });
      return true;
    } catch (error) {
      if (import.meta.env.DEV) console.error('Profile image upload failed:', error);
      return false;
    }
  };

  const completeOnboarding = async () => {
    if (user && session?.user) {
      setUser({ ...user, onboardingCompleted: true });
      
      try {
        await authService.updateProfile(session.user.id, { onboarding_completed: true });
      } catch (error) {
        if (import.meta.env.DEV) console.error('Failed to sync onboarding completion:', error);
      }
    }
  };

  const calculateProfileCompletion = () => {
    if (!user) return { userCompleted: 0, adminPending: 0, total: 0 };
    let userCompleted = 0;
    if (user.name) userCompleted += 25;
    if (user.phone) userCompleted += 25;
    if (user.email) userCompleted += 25;
    if (user.cnicUploaded) userCompleted += 25;
    return { userCompleted, adminPending: 0, total: userCompleted };
  };

  const getCNICFiles = () => {
    if (user?.cnicFiles) {
      return { frontPreview: user.cnicFiles.frontPreview, backPreview: user.cnicFiles.backPreview };
    }
    return null;
  };

  // Resource operations
  const addResource = (resourceData: Omit<Resource, 'id'>) => {
    if (user) {
      const newResource: Resource = { ...resourceData, id: Date.now().toString() };
      setUser({ ...user, resources: [...(user.resources || []), newResource] });
    }
  };

  const updateResource = (id: string, updates: Partial<Resource>) => {
    if (user?.resources) {
      setUser({
        ...user,
        resources: user.resources.map(r => r.id === id ? { ...r, ...updates } : r)
      });
    }
  };

  const removeResource = (id: string) => {
    if (user?.resources) {
      setUser({ ...user, resources: user.resources.filter(r => r.id !== id) });
    }
  };

  // Job application
  const addJobApplication = async (applicationData: Omit<JobApplication, 'id'>) => {
    if (!user || !session?.user) return;
    
    try {
      const { applicationsService } = await import('@/services/supabase');
      const { application, error } = await applicationsService.applyToJob({
        job_id: applicationData.jobId,
        applicant_id: session.user.id,
        cover_letter: applicationData.coverLetter,
        expected_salary: applicationData.proposedRate,
        availability_date: applicationData.availabilityDate,
      });
      
      if (error) {
        if (import.meta.env.DEV) console.error('Failed to submit application:', error);
        return;
      }

      const newApplication: JobApplication = {
        ...applicationData,
        id: application?.id || Date.now().toString()
      };
      
      setUser({
        ...user,
        jobApplications: [...(user.jobApplications || []), newApplication]
      });
    } catch (error) {
      if (import.meta.env.DEV) console.error('Failed to add job application:', error);
    }
  };

  return {
    updateUser,
    updateCNICVerification,
    uploadCNICFiles,
    uploadProfileImage,
    completeOnboarding,
    calculateProfileCompletion,
    getCNICFiles,
    addResource,
    updateResource,
    removeResource,
    addJobApplication,
  };
}
